import * as React from 'react';
import { MY_ACTIVITY_PAGE } from 'constants/pages';
import { NotificationActionTypesEnum, NotificationStatusesEnum } from 'types/generated/client';
import Link from 'components/Link';
import PlayRequestNotificationCard, {
  type PlayRequestPillTone,
} from './PlayRequestNotificationCard';
import { NotificationTypeSelectorProps } from '../types';

export type PlayRequestActionType =
  | NotificationActionTypesEnum.PlayRequestReceived
  | NotificationActionTypesEnum.PlayRequestAccepted
  | NotificationActionTypesEnum.PlayRequestDeclined
  | NotificationActionTypesEnum.PlayRequestAutoCancelled;

/** The four play-request notifications differ only in these fields. */
interface PlayRequestVariant {
  pill: { label: string; tone: PlayRequestPillTone };
  /** A function when the copy depends on the request (game vs availability). */
  title: string | ((isGameRequest: boolean) => string);
  /** Which free-text field to surface, or a fixed string. */
  message: 'requestMessage' | 'responseMessage' | string;
  /** Whose profile the embedded player card shows. */
  playerProfile: 'owner' | 'requester';
}

const VARIANTS: Record<PlayRequestActionType, PlayRequestVariant> = {
  [NotificationActionTypesEnum.PlayRequestReceived]: {
    pill: { label: 'Play Request', tone: 'received' },
    title: (isGameRequest) =>
      isGameRequest ? 'Requested to play your game' : 'Requested to play from your availability',
    message: 'requestMessage',
    // The host received this — the embedded player card shows the requester.
    playerProfile: 'requester',
  },
  [NotificationActionTypesEnum.PlayRequestAccepted]: {
    pill: { label: 'Request Accepted', tone: 'accepted' },
    title: 'Request was accepted',
    message: 'responseMessage',
    playerProfile: 'owner',
  },
  [NotificationActionTypesEnum.PlayRequestDeclined]: {
    pill: { label: 'Request Declined', tone: 'declined' },
    title: 'Your request was declined.',
    message: 'responseMessage',
    playerProfile: 'owner',
  },
  [NotificationActionTypesEnum.PlayRequestAutoCancelled]: {
    pill: { label: 'Request Canceled', tone: 'cancelled' },
    title: 'Your request was canceled automatically.',
    message: 'All the spots were taken.',
    playerProfile: 'owner',
  },
};

interface Props extends NotificationTypeSelectorProps {
  status: NotificationStatusesEnum;
  actionType: PlayRequestActionType;
}

/**
 * All four play-request notifications (received / accepted / declined /
 * auto-cancelled). They share one card and differ only by the pill, title,
 * which free-text field they surface and whose profile the embedded player card
 * shows — so the action type selects a variant rather than each getting its own
 * near-identical component.
 */
export default function PlayRequestNotification({ notification, status, actionType }: Props) {
  const primaryEntity = notification?.notificationDetails?.primaryEntity;
  const actorFullName = primaryEntity?.actingUserProfile?.fullName ?? 'A player';
  const actorCoachStatus = primaryEntity?.actingUserProfile?.coachStatus;
  const playRequest = primaryEntity?.playRequest;

  const variant = VARIANTS[actionType];
  const isGameRequest = Boolean(playRequest?.event);

  const title = typeof variant.title === 'function' ? variant.title(isGameRequest) : variant.title;
  const message =
    variant.message === 'requestMessage'
      ? playRequest?.requestMessage
      : variant.message === 'responseMessage'
      ? playRequest?.responseMessage
      : variant.message;

  return (
    <Link href={MY_ACTIVITY_PAGE} className="block">
      <PlayRequestNotificationCard
        status={status}
        actorName={actorFullName}
        actorCoachStatus={actorCoachStatus}
        pill={variant.pill}
        title={title}
        message={message}
        playRequest={playRequest}
        playerProfile={variant.playerProfile}
      />
    </Link>
  );
}
